// Main converter page. This file is intentionally large (~600 lines). To keep
// it navigable, JSX is divided into clearly-marked sections (search for
// "═══" to jump between them). New brand colors live in src/theme/colors.js;
// see frontend/README.md "Styling" section.

import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import {
  Paper, Typography, Button, LinearProgress, Box, Chip,
  List, ListItem, ListItemText, ListItemSecondaryAction, IconButton,
  Divider, Alert, Menu, MenuItem
} from '@mui/material';
import { Download, Delete, Email, PictureAsPdf, Group, FileDownloadOutlined } from '@mui/icons-material';
import { useApp } from '../context/AppContext';
import { axiosRetry } from '../context/AppContext';
import { extractApiError } from '../utils/apiClient';
import { isSubscriptionActive, isSubscriptionActiveOrCanceling } from '../utils/subscriptionHelpers';
import { MAX_FILE_SIZE_BYTES, PAID_PLAN_MAX_FILES } from '../utils/constants';
import { trackEvent, TRACKED_EVENTS } from '../utils/analytics';
import { hero, supportedFormatChips } from '../data/content';
import { hideHeaderAuth } from '../utils/embedOptions';
import { colors, radii, fonts, motion } from '../theme/colors';

const PLAN_NAMES = { free: 'Free Plan', starter: 'Starter', pro: 'Pro', team: 'Team' };

// The page is a stack of full-bleed white/paper bands, not one card. `SHELL`
// is the shared content column; `BAND` is the outer full-width wrapper.
const SHELL = { maxWidth: 1140, mx: 'auto' };
const BAND = { px: 2.5 };

// Small mono chip used inside the dropzone — a tighter variant of the pill
// chips in the hero (6px radius, 11px type).
const dropChip = (accent) => ({
  fontFamily: fonts.mono,
  fontSize: '0.6875rem',
  borderRadius: '6px',
  padding: '4px 10px',
  border: '1px solid',
  ...(accent
    ? { bgcolor: colors.goldTint, color: colors.goldInk, borderColor: colors.gold }
    : { bgcolor: colors.surface, color: colors.textSecondary, borderColor: colors.borderSubtle }),
});

// Account-card row: label left, value right, hairline rule between.
function AcctRow({ label, children, last }) {
  return (
    <Box
      sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2,
        py: 1.125, borderBottom: last ? 'none' : `1px solid ${colors.borderSubtle}`,
      }}
    >
      <Typography sx={{ fontSize: '0.84375rem', color: colors.textSecondary }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{children}</Box>
    </Box>
  );
}

export default function ConversionPage() {
  const {
    isCloudFrontUrl, isSubscription, GUEST_DEMO_LIMIT, SIGNUP_DEMO_LIMIT,
    authOk, email, planType, subscriptionStatus, conversionsRemaining,
    monthlyConversionsUsed, monthlyConversionLimit,
    requiresAuth, isCheckingAuth, password, setPassword,
    handleUnlock, handleLogout,
    setAuthModalOpen, setAuthTab, setCurrentPage, setSnackbar,
    apiClient,
  } = useApp();

  // Resolves an endpoint path for the shared apiClient (which owns the base
  // URL and auth headers): CloudFront deployments route the API under /api,
  // and legacy password mode passes the password as a query param as well.
  const apiPath = useCallback((path, includeLegacyAuth = false) => {
    let resolved = isCloudFrontUrl ? `/api${path}` : path;
    if (includeLegacyAuth && !isSubscription && password && isCloudFrontUrl) {
      resolved += `${resolved.includes('?') ? '&' : '?'}auth=${encodeURIComponent(password)}`;
    }
    return resolved;
  }, [isCloudFrontUrl, isSubscription, password]);

  // Local conversion state
  const [files, setFiles] = useState([]);
  const [converting, setConverting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [demoMsgCount, setDemoMsgCount] = useState(() => {
    const stored = parseInt(window.localStorage.getItem('demoMsgCount') || '0', 10);
    return Number.isNaN(stored) ? 0 : stored;
  });
  const [showPaywall, setShowPaywall] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);

  const resultsRef = useRef(null);

  // Capture Google Ads tracking parameters
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    const trackingKeys = ['gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
    const trackingData = {};
    let hasTracking = false;
    try {
      Object.assign(trackingData, JSON.parse(window.localStorage.getItem('ad_tracking_params') || '{}'));
    } catch (_) {}
    trackingKeys.forEach(key => { const val = params.get(key); if (val) { trackingData[key] = val; hasTracking = true; } });
    if (hasTracking) window.localStorage.setItem('ad_tracking_params', JSON.stringify(trackingData));
  }, []);

  useEffect(() => {
    try { window.localStorage.setItem('demoMsgCount', String(demoMsgCount)); } catch (_) {}
  }, [demoMsgCount]);

  useEffect(() => {
    if (authOk) { setShowPaywall(false); return; }
    if (isSubscription && demoMsgCount >= GUEST_DEMO_LIMIT) setShowPaywall(true);
  }, [authOk, demoMsgCount, isSubscription, GUEST_DEMO_LIMIT]);

  const demoRemaining = Math.max(GUEST_DEMO_LIMIT - demoMsgCount, 0);
  const isDemoLocked = isSubscription && !authOk && demoMsgCount >= GUEST_DEMO_LIMIT;
  const isPaywallActive = isSubscription && !authOk && (showPaywall || isDemoLocked);
  const maxFiles = (authOk && ['starter', 'pro', 'team'].includes(planType)) ? PAID_PLAN_MAX_FILES : GUEST_DEMO_LIMIT;
  const successfulConversions = results.filter(r => r.status === 'success').length;
  const failedConversions = results.length > 0 ? results.length - successfulConversions : 0;

  // The embed has no pricing section to scroll to, so every upgrade path is the
  // signup modal.
  const handleStartForFree = useCallback(() => {
    if (isPaywallActive) { setAuthModalOpen(true); setAuthTab('signup'); return; }
    if (demoRemaining > 0 || authOk) { return; }
    setAuthModalOpen(true); setAuthTab('signup');
  }, [isPaywallActive, demoRemaining, authOk, setAuthModalOpen, setAuthTab]);

  const handleAccountButton = useCallback(() => {
    if (authOk) setAccountMenuAnchor((prev) => (prev ? null : document.getElementById('account-btn')));
    else { setAuthModalOpen(true); setAuthTab('signin'); }
  }, [authOk, setAuthModalOpen, setAuthTab]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const tooMany = rejectedFiles.some(r => r.errors.some(e => e.code === 'too-many-files'));
      if (tooMany) { setSnackbar({ open: true, message: `You can only upload up to ${maxFiles} files at a time.`, severity: 'warning' }); return; }
      if (rejectedFiles.some(r => r.errors.some(e => e.code === 'file-invalid-type'))) {
        setSnackbar({ open: true, message: 'Please only select .eml or .msg files', severity: 'warning' });
      }
    }
    const allowed = acceptedFiles.filter(f => { const n = f.name.toLowerCase(); return n.endsWith('.eml') || n.endsWith('.msg'); });
    if (!authOk || planType === 'free') {
      const oversized = allowed.filter(f => f.size > MAX_FILE_SIZE_BYTES);
      if (oversized.length > 0) { setSnackbar({ open: true, message: 'Files must be under 10MB. Upgrade for unlimited size.', severity: 'warning' }); return; }
    }
    if (allowed.length > 0) {
      if (isSubscription && !authOk) {
        if (demoMsgCount + allowed.length > GUEST_DEMO_LIMIT) {
          setShowPaywall(true); setAuthModalOpen(true); setAuthTab('signup');
          setSnackbar({ open: true, message: `Guest demo limit reached. Create an account for ${SIGNUP_DEMO_LIMIT} more free conversions.`, severity: 'info' });
          return;
        }
      }
      setFiles(prevFiles => {
        if (prevFiles.length + allowed.length > maxFiles) {
          setSnackbar({ open: true, message: `You can only have up to ${maxFiles} files.`, severity: 'warning' });
          return prevFiles;
        }
        return [...prevFiles, ...allowed];
      });
    }
  }, [authOk, demoMsgCount, isSubscription, maxFiles, planType, GUEST_DEMO_LIMIT, SIGNUP_DEMO_LIMIT, setSnackbar, setAuthModalOpen, setAuthTab]);

  const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
    onDrop, accept: { 'message/rfc822': ['.eml', '.msg'], 'application/vnd.ms-outlook': ['.msg'] },
    multiple: true, maxFiles,
  });

  const removeFile = (index) => setFiles(files.filter((_, i) => i !== index));

  const uploadFilesViaS3 = async (filesToUpload) => {
    const keys = [];
    for (const f of filesToUpload) {
      const presign = await axiosRetry(
        () => apiClient.post(apiPath('/upload-url', true), { filename: f.name, content_type: f.type || 'application/octet-stream' }),
        'presign upload-url', 4, 300
      );
      const { url, key } = presign.data || {};
      if (!url || !key) throw new Error('Failed to obtain upload URL');
      // Raw axios on purpose: the presigned S3 PUT must not carry auth headers.
      await axiosRetry(() => axios.put(url, f, { headers: { 'Content-Type': f.type || 'application/octet-stream' } }), 's3 put', 4, 400);
      keys.push(key);
    }
    return keys;
  };

  const convertFilesDirect = async (filesToConvert) => {
    const aggregated = [];
    let finalSessionId = null;
    const convertUrl = apiPath('/convert', true);
    for (let i = 0; i < filesToConvert.length; i++) {
      const f = filesToConvert[i];
      const form = new FormData();
      form.append('file', f);
      try {
        const res = await axiosRetry(
          () => apiClient.post(convertUrl, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
          'direct convert', 3, 500
        );
        const data = res?.data || {};
        const resArr = Array.isArray(data.results) ? data.results : (data.result ? [data.result] : []);
        const r = resArr[0] || {};
        const sid = data.sessionId || data.session_id || r.sessionId || r.session_id;
        if (sid) finalSessionId = sid;
        aggregated.push(r.success || r.status === 'success'
          ? { filename: r.originalName || r.filename || f.name, status: 'success', subject: 'No Subject', session_id: sid, pdf_filename: r.pdfName || r.pdf_filename, message: 'Conversion successful (direct)' }
          : { filename: r.originalName || r.filename || f.name, status: 'error', message: r.message || 'PDF conversion failed', subject: null, session_id: sid, pdf_filename: r.pdfName || r.pdf_filename });
      } catch (err) {
        if (err?.response?.status === 429) {
          const errData = err?.response?.data || {};
          aggregated.push({ filename: f.name, status: 'error', message: `Monthly limit exceeded (${errData.conversions_used || 0}/${errData.limit || 0}). Please upgrade.`, subject: null, session_id: null, pdf_filename: null });
        } else {
          aggregated.push({ filename: f.name, status: 'error', message: err?.response?.data?.message || err?.response?.data?.error || err.message || 'PDF conversion failed', subject: null, session_id: null, pdf_filename: null });
        }
      }
      setProgress(70 + Math.round(30 * ((i + 1) / filesToConvert.length)));
    }
    return { results: aggregated, sessionId: finalSessionId };
  };

  const convertFiles = async () => {
    if (files.length === 0) return;
    const guestBatchCount = files.length;
    if (isSubscription && !authOk) {
      if (demoMsgCount >= GUEST_DEMO_LIMIT || demoMsgCount + guestBatchCount > GUEST_DEMO_LIMIT) {
        setShowPaywall(true); return;
      }
    }
    if (requiresAuth && !authOk) {
      setSnackbar({ open: true, message: isSubscription ? 'Please log in before converting.' : 'Please unlock with the password before converting.', severity: 'warning' });
      return;
    }
    trackEvent(TRACKED_EVENTS.CONVERSION_STARTED, { file_count: files.length, authed: authOk });
    setConverting(true); setProgress(0); setResults([]);
    const batchSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    try {
      setProgress(10);
      let keys;
      try {
        keys = await uploadFilesViaS3(files);
      } catch (_) {
        const direct = await convertFilesDirect(files);
        const directSuccesses = direct.results.filter(r => r.status === 'success').length;
        if (directSuccesses > 0) trackEvent(TRACKED_EVENTS.CONVERSION_SUCCEEDED, { file_count: directSuccesses, path: 'direct' });
        setResults(direct.results); setSessionId(direct.sessionId || batchSessionId); setProgress(100); return;
      }
      setProgress(70);
      const s3Url = apiPath('/convert-s3', true);
      const aggregated = [];
      let finalSessionId = null;
      for (let i = 0; i < keys.length; i++) {
        try {
          const conv = await axiosRetry(
            () => apiClient.post(s3Url, { keys: [keys[i]], session_id: batchSessionId }),
            'convert-s3', 3, 400
          );
          const r = (conv.data?.results || [])[0];
          if (r?.status === 'success') {
            aggregated.push({ filename: r.filename || 'file', status: 'success', subject: 'No Subject', session_id: r.session_id, pdf_filename: r.pdf_filename, message: 'Conversion successful (S3)' });
            if (!finalSessionId && r.session_id) finalSessionId = r.session_id;
          } else {
            aggregated.push({ filename: r?.filename || 'file', status: 'error', message: r?.message || 'PDF conversion failed', subject: null, session_id: null, pdf_filename: null });
          }
        } catch (err) {
          aggregated.push({ filename: keys[i].split('/').pop() || 'file', status: 'error', message: err?.response?.data?.message || err.message || 'PDF conversion failed', subject: null, session_id: null, pdf_filename: null });
        }
        setProgress(70 + Math.round(30 * ((i + 1) / keys.length)));
      }
      const s3Successes = aggregated.filter(r => r.status === 'success').length;
      if (s3Successes > 0) trackEvent(TRACKED_EVENTS.CONVERSION_SUCCEEDED, { file_count: s3Successes, path: 's3' });
      setResults(aggregated); setSessionId(finalSessionId || batchSessionId); setProgress(100);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) setSnackbar({ open: true, message: 'Unauthorized. Please enter the correct password.', severity: 'error' });
      else setSnackbar({ open: true, message: extractApiError(error) || 'Error during conversion. Please try again.', severity: 'error' });
    } finally {
      if (isSubscription && !authOk && guestBatchCount > 0) {
        setDemoMsgCount(prev => {
          const updated = Math.min(GUEST_DEMO_LIMIT, prev + guestBatchCount);
          if (updated >= GUEST_DEMO_LIMIT) { setShowPaywall(true); setAuthModalOpen(true); setAuthTab('signup'); }
          return updated;
        });
      }
      setConverting(false);
    }
  };

  const downloadFile = async (sid, pdfFilename, originalFilename) => {
    const encodedName = encodeURIComponent(pdfFilename).replace(/%2F/g, '/');
    try {
      const res = await apiClient.get(apiPath(`/download/${sid}/${encodedName}`), { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const baseName = (pdfFilename || originalFilename || 'converted.pdf').replace(/\.(eml|msg)$/i, '');
      link.download = /\.pdf$/i.test(baseName) ? baseName : `${baseName}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setSnackbar({ open: true, message: `Download failed: ${extractApiError(err)}`, severity: 'error' });
    }
  };

  const downloadAllFiles = async () => {
    if (!sessionId) return;
    try {
      const res = await apiClient.get(apiPath(`/download-all/${sessionId}`), { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl; link.download = `converted_pdfs_${sessionId}.zip`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setSnackbar({ open: true, message: `Download failed: ${extractApiError(err)}`, severity: 'error' });
    }
  };

  const clearAll = () => {
    setFiles([]); setResults([]); setProgress(0); setSessionId(null);
    setSnackbar({ open: true, message: 'Selection cleared.', severity: 'info' });
  };

  const getStatusColor = (status) => ({ success: 'success', error: 'error' }[status] || 'default');
  const getStatusIcon = (status) => status === 'success' ? <PictureAsPdf color="success" /> : <Email color={status === 'error' ? 'error' : 'inherit'} />;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box component="div" sx={{ minHeight: '100vh', bgcolor: colors.page }}>

      {/* ═══════════════════ Header (account button) ═══════════════════
           The account button is the header's only content, so ?hideAuth=1 —
           passed by a partner who places the standalone ?embed=auth button in
           their own nav and would otherwise show two — collapses the header
           entirely. */}
      {!hideHeaderAuth && (
        <Box component="header" sx={{ ...BAND, py: 2 }}>
          <Box sx={{ ...SHELL, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
            <Box>
              <Button id="account-btn" variant="contained" color="secondary" size="small" onClick={handleAccountButton}
                sx={{ borderRadius: `${radii.control}px`, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.8125rem', px: 3, py: 1.6 }}>
                {authOk ? (email || window.localStorage.getItem('userEmail') || 'Account') : 'Sign in / Create account'}
              </Button>
              <Menu anchorEl={accountMenuAnchor} open={Boolean(accountMenuAnchor)} onClose={() => setAccountMenuAnchor(null)}>
                <MenuItem onClick={() => { setAccountMenuAnchor(null); setCurrentPage('billing'); }}>Billing portal</MenuItem>
                <MenuItem onClick={() => { handleLogout(); setAccountMenuAnchor(null); }}>Logout</MenuItem>
              </Menu>
            </Box>
          </Box>
        </Box>
      )}

      {/* ═══════════════════ Hero band (white) ═══════════════════ */}
      <Box component="section" aria-label="MSG to PDF converter" sx={{ ...BAND, bgcolor: colors.page, pt: { xs: 5, md: 9 }, pb: 7 }}>
        <Box sx={SHELL}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.25fr 0.85fr' }, gap: { xs: 4, md: 6 }, alignItems: 'start' }}>
            <Box>
              <Box component="span"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1, mb: 3,
                  fontFamily: fonts.mono, fontSize: '0.8125rem', letterSpacing: '0.02em',
                  color: colors.textSecondary, bgcolor: colors.paper,
                  border: `1px solid ${colors.borderSubtle}`, borderRadius: `${radii.pill}px`, px: 2, py: 0.9,
                }}>
                {hero.eyebrow.from}
                <Box component="span" sx={{ color: colors.teal500 }}>&rarr;</Box>
                {hero.eyebrow.to}
              </Box>

              {/* Statement in ink, the turn in slate-teal — the house pattern. */}
              <Typography variant="h1" component="h1"
                sx={{ fontSize: { xs: '2.25rem', md: '2.875rem' }, color: colors.ink, mb: 2.5 }}>
                {hero.headline}
                <Box component="span" sx={{ display: 'block', color: colors.teal700 }}>{hero.headlineAccent}</Box>
              </Typography>

              <Typography sx={{ fontSize: '0.96875rem', lineHeight: 1.6, color: colors.textSecondary, maxWidth: 520, mb: 2 }}>
                {hero.description}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {/* One uniform chip — the reference site doesn't color-code formats. */}
                {supportedFormatChips.map((ext) => (
                  <Chip key={ext} label={ext} size="small"
                    sx={{ bgcolor: colors.paper, color: colors.textSecondary, border: `1px solid ${colors.borderSubtle}`, fontSize: '0.8125rem', height: 30 }} />
                ))}
              </Box>

              <Typography sx={{ fontSize: '0.8125rem', color: colors.teal500, mb: 3 }}>{hero.trustLine}</Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <Button variant="contained" onClick={() => handleStartForFree()}>{hero.ctaPrimary}</Button>
              </Box>
            </Box>

            {/* ─── Account panel ─── */}
            <Paper variant="outlined" sx={{ p: '22px 24px', borderRadius: `${radii.panel}px`, borderColor: colors.borderSubtle, bgcolor: colors.paper }}>
              <Typography sx={{ fontFamily: fonts.mono, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.teal700, mb: 1 }}>
                Account
              </Typography>

              <AcctRow label="Status">
                <Typography sx={{ fontSize: '0.84375rem', fontWeight: 600, color: colors.ink, fontFamily: authOk ? fonts.mono : fonts.body }}>
                  {authOk ? (email || window.localStorage.getItem('userEmail') || 'Signed in') : 'Guest User'}
                </Typography>
              </AcctRow>

              <AcctRow label="Current plan">
                <Typography sx={{ fontSize: '0.84375rem', fontWeight: 600, color: colors.ink }}>
                  {!authOk ? 'Guest Demo' : (isSubscriptionActiveOrCanceling(subscriptionStatus) ? (PLAN_NAMES[planType] || `${planType?.charAt(0).toUpperCase() + planType?.slice(1)} Plan`) : 'Free Tier')}
                </Typography>
                {authOk && (
                  <Chip label={isSubscriptionActive(subscriptionStatus) ? 'ACTIVE' : (subscriptionStatus === 'canceling' ? 'CANCELING' : 'FREE')}
                    size="small" color={isSubscriptionActiveOrCanceling(subscriptionStatus) ? 'success' : 'default'} variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, ...(!isSubscriptionActiveOrCanceling(subscriptionStatus) && { color: colors.textSecondary, borderColor: colors.borderSubtle }) }} />
                )}
              </AcctRow>

              <AcctRow label="Usage" last>
                <Typography sx={{ fontSize: '0.84375rem', fontWeight: 600, color: colors.ink }}>
                  {authOk ? (monthlyConversionsUsed || 0) : (GUEST_DEMO_LIMIT - demoRemaining)} / {authOk ? monthlyConversionLimit : GUEST_DEMO_LIMIT} used
                </Typography>
              </AcctRow>

              <LinearProgress variant="determinate"
                value={authOk ? Math.min(100, ((monthlyConversionsUsed || 0) / monthlyConversionLimit) * 100) : Math.min(100, ((GUEST_DEMO_LIMIT - demoRemaining) / GUEST_DEMO_LIMIT) * 100)}
                sx={{ mt: 1.5, height: 6, bgcolor: colors.powderTint, '& .MuiLinearProgress-bar': { bgcolor: conversionsRemaining > 0 ? colors.teal700 : colors.error500 } }} />

              {results.length > 0 && (
                <Button variant="outlined" fullWidth sx={{ mt: 2 }} onClick={() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                  View conversions
                </Button>
              )}
              {planType?.includes('team') && (
                <Button variant="outlined" fullWidth startIcon={<Group />}
                  onClick={() => setCurrentPage('billing')} sx={{ mt: 1 }}>
                  Manage Team
                </Button>
              )}
            </Paper>
          </Box>

          {/* ═══════════════════ Legacy password gate (non-subscription mode only) ═══════════════════ */}
          {!isSubscription && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 4, flexWrap: 'wrap' }}>
              <input type="password" placeholder="Enter access password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleUnlock(); }}
                style={{ flex: 1, minWidth: 220, padding: '12px 14px', borderRadius: radii.control, border: `1px solid ${colors.borderSubtle}`, background: colors.surface, fontFamily: fonts.body, color: colors.textPrimary }} />
              <Button variant="contained" onClick={handleUnlock} disabled={isCheckingAuth}>
                {isCheckingAuth ? 'Checking...' : 'Unlock'}
              </Button>
              <Button variant="outlined" onClick={() => window.localStorage.removeItem('appPassword')}>Clear</Button>
              {!authOk ? <Chip label="Locked" color="warning" size="small" /> : <Chip label="Unlocked" color="success" size="small" />}
            </Box>
          )}

          {/* ═══════════════════ Paywall overlay + Dropzone ═══════════════════ */}
          <Box sx={{ position: 'relative', mt: 5 }}>
            {isPaywallActive && (
              <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255, 255, 255, 0.94)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: 3, gap: 2 }}>
                <Typography variant="h5">Free demo limit reached</Typography>
                <Typography variant="body1" color="text.secondary">
                  Guest demo is {GUEST_DEMO_LIMIT} files. Create an account for {SIGNUP_DEMO_LIMIT} more free conversions or upgrade for unlimited access.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Button variant="contained" onClick={() => { setAuthModalOpen(true); setAuthTab('signup'); }}>Create account</Button>
                </Box>
              </Box>
            )}

            <Box sx={{ opacity: isPaywallActive ? 0.35 : 1, pointerEvents: isPaywallActive ? 'none' : 'auto' }}>
              {/* ─── Drop Zone. The drag/click target is the inner block only, so the
                   buttons below it don't re-open the file picker. ─── */}
              <Box sx={{
                border: '2px dashed', borderColor: isDragActive ? colors.seal : colors.powder,
                borderRadius: `${radii.dropzone}px`, width: '100%', maxWidth: 720, mx: 'auto',
                p: { xs: 3, md: 4 }, bgcolor: isDragActive ? colors.sealTint : colors.paper,
                transition: `border-color ${motion.durationBase} ${motion.easeStandard}, background-color ${motion.durationBase} ${motion.easeStandard}`,
              }}>
                <Box {...getRootProps()} sx={{ cursor: 'pointer', textAlign: 'center' }}>
                  <input {...getInputProps()} />
                  <FileDownloadOutlined sx={{ fontSize: 30, color: colors.seal, mb: 1 }} />
                  <Typography sx={{ fontSize: '0.96875rem', color: colors.ink }}>
                    {isDragActive ? 'Drop the files here…' : 'Drag & drop your files, or '}
                    {!isDragActive && (
                      <Box component="span" sx={{ color: colors.seal, textDecoration: 'underline', cursor: 'pointer', '&:hover': { color: colors.sealDark } }}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!isPaywallActive && openFilePicker) openFilePicker(); else { setAuthModalOpen(true); setAuthTab('signin'); } }}>
                        browse to upload
                      </Box>
                    )}
                  </Typography>

                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {['.eml', '.msg', 'PDF output'].map(l => (
                      <Box component="span" key={l} sx={dropChip(false)}>{l}</Box>
                    ))}
                    {/* Gold marks a constraint worth noticing — its one job in this palette. */}
                    <Box component="span" sx={dropChip(true)}>
                      {authOk && planType !== 'free' ? 'Unlimited size' : 'Max 10MB'}
                    </Box>
                  </Box>
                </Box>

                {isSubscription && !authOk && (
                  <Typography sx={{ mt: 2.5, fontSize: '0.8125rem', color: colors.teal500, textAlign: 'center' }}>
                    Guest remaining: {demoRemaining}/{GUEST_DEMO_LIMIT} files
                  </Typography>
                )}

                <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button variant="contained" onClick={convertFiles}
                    disabled={files.length === 0 || converting || (requiresAuth && !authOk) || isPaywallActive}
                    startIcon={<PictureAsPdf />}>
                    {converting ? 'Converting...' : 'Convert to PDF'}
                  </Button>
                  {files.length > 0 && <Button variant="outlined" onClick={clearAll} disabled={converting}>Clear All</Button>}
                  {successfulConversions >= 1 && (
                    <Button variant="contained" color="secondary" onClick={downloadAllFiles} startIcon={<Download />}>
                      Download All PDFs
                    </Button>
                  )}
                </Box>
              </Box>

              {/* ─── Selected-files list ─── */}
              {files.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2.5, mt: 3, maxWidth: 720, mx: 'auto', borderRadius: `${radii.panel}px`, borderColor: colors.borderSubtle, bgcolor: colors.surface }}>
                  <Typography variant="h6" sx={{ color: colors.textPrimary }} gutterBottom>Selected Files ({files.length})</Typography>
                  <List>
                    {files.map((file, index) => (
                      <ListItem key={`${file.name}-${file.size}-${index}`} divider sx={{ borderColor: colors.borderSubtle }}>
                        <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(1)} KB`}
                          primaryTypographyProps={{ noWrap: true, sx: { color: colors.textPrimary, fontFamily: fonts.mono, fontSize: '0.875rem' } }}
                          secondaryTypographyProps={{ sx: { color: colors.textSecondary } }} />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => removeFile(index)} disabled={converting} sx={{ color: colors.textMuted, '&:hover': { color: colors.error500 } }}>
                            <Delete />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {converting && (
                <Box sx={{ mt: 3, maxWidth: 720, mx: 'auto' }}>
                  <Typography variant="body2" gutterBottom sx={{ color: colors.textPrimary }}>Converting files...</Typography>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="caption" sx={{ mt: 1, color: colors.textSecondary }}>{progress}% uploaded</Typography>
                </Box>
              )}

              {/* ─── Conversion results list (per-file status + download buttons) ─── */}
              {results.length > 0 && (
                <Box ref={resultsRef} sx={{ maxWidth: 720, mx: 'auto' }}>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom sx={{ color: colors.textPrimary }}>Conversion Results</Typography>
                  <List>
                    {results.map((result, index) => (
                      <ListItem key={`${result.filename}-${index}`} divider sx={{ borderColor: colors.borderSubtle }}>
                        <ListItemText
                          primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{getStatusIcon(result.status)}<Typography sx={{ color: colors.textPrimary, fontFamily: fonts.mono, fontSize: '0.875rem' }}>{result.filename}</Typography><Chip label={result.status} color={getStatusColor(result.status)} size="small" variant="filled" sx={{ color: colors.textInverse, height: 20, '& .MuiChip-label': { px: 1, py: 0 } }} /></Box>}
                          secondary={result.status === 'error' ? result.message : result.subject}
                          secondaryTypographyProps={{ sx: { color: colors.textSecondary } }}
                        />
                        {result.status === 'success' && result.session_id && result.pdf_filename && (
                          <ListItemSecondaryAction>
                            <Button variant="contained" size="small" startIcon={<Download />}
                              onClick={() => downloadFile(result.session_id, result.pdf_filename, result.filename)}>
                              Download PDF
                            </Button>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                  </List>
                  {successfulConversions > 0 && <Alert severity="success" sx={{ mt: 2 }}>Successfully converted {successfulConversions} of {results.length} files. Use "Download All PDFs" to get them as a ZIP file.</Alert>}
                  {failedConversions > 0 && <Alert severity="warning" sx={{ mt: 2 }}>{failedConversions} file{failedConversions === 1 ? '' : 's'} failed to convert. The successful ones are still available to download.</Alert>}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

    </Box>
  );
}
